import { PROJECT_BULK_MAX } from '@crm/contracts';
import { useMutation } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import type { PipelineView } from '../../lib/client.ts';
import { stageOptions } from '../../lib/record-data.ts';
import { FieldChip } from '../record-table/quick-filters.tsx';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

/** What a bulk add answers (projects, deals and jobs alike): how many joined, and why others did not. */
export interface BulkAddResult {
  added: number;
  skipped: Array<{ reason: 'already_in_pipeline' | 'not_found' | 'no_permission' }>;
}

/**
 * The bar above the Projects, Deals and Jobs tables while rows are ticked: how many, Add to
 * pipeline, Clear. It only ADDS the records to another pipeline (`POST /…:add-to-pipeline`);
 * stages are never changed in bulk, so a record already in that pipeline stays where it is.
 */
export function BulkPipelineBar({
  count,
  onAdd,
  onClear,
}: {
  count: number;
  onAdd: () => void;
  onClear: () => void;
}) {
  return (
    <div
      className="bg-brand/5 border-brand/30 flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm"
      data-testid="bulk-bar"
    >
      <span className="font-medium" data-testid="bulk-count">
        {count} selected
        {count > PROJECT_BULK_MAX && ` (at most ${PROJECT_BULK_MAX.toLocaleString()} at once)`}
      </span>
      <Button
        size="sm"
        className="ml-auto"
        onClick={onAdd}
        disabled={count > PROJECT_BULK_MAX}
        data-testid="bulk-add-to-pipeline"
      >
        Add to pipeline…
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear} aria-label="Clear selection">
        <X /> Clear
      </Button>
    </div>
  );
}

const firstOpen = (p: PipelineView | undefined) => p?.stages.find((s) => !s.isTerminal)?.id;

/**
 * Pick a pipeline, then the stage the records join it at — with the lists' stage filter chip
 * itself (`FieldChip`, named after the pipeline; its popover has the search, the stage tree and
 * the pills), one choice only, open stages only.
 */
export function AddToPipelineDialog({
  noun,
  ids,
  pipelines,
  current,
  add: addTo,
  onClose,
  onDone,
}: {
  /** What the records are, singular ("project", "deal", "job"). */
  noun: string;
  ids: string[];
  /** The pipelines the records may join, the default first when it is one of them. */
  pipelines: readonly PipelineView[];
  /** The pipeline the list shows: every listed record is in it, so another is offered first. */
  current: string | undefined;
  add: (body: { pipelineId: string; stageId: string; ids: string[] }) => Promise<BulkAddResult>;
  onClose: () => void;
  onDone: (result: BulkAddResult) => void;
}) {
  const [pipelineId, setPipelineId] = useState(
    () => (pipelines.find((p) => p.id !== current) ?? pipelines[0])?.id,
  );
  const pipeline = pipelines.find((p) => p.id === pipelineId);
  const [stageId, setStageId] = useState(() => firstOpen(pipeline));
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => addTo({ pipelineId: pipelineId!, stageId: stageId!, ids }),
    onSuccess: onDone,
    onError: (err) => setError(describeError(err)),
  });
  const n = ids.length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Add {n} {noun}
            {n === 1 ? '' : 's'} to a pipeline
          </DialogTitle>
          <DialogDescription>
            Each {noun} joins the pipeline at this stage. One already in it is left where it is.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            add.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Pipeline</span>
            <Select
              value={pipelineId ?? ''}
              onValueChange={(id) => {
                setPipelineId(id);
                setStageId(firstOpen(pipelines.find((p) => p.id === id)));
              }}
            >
              <SelectTrigger aria-label="Pipeline">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {pipeline && (
            <div className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Stage</span>
              {/* The Projects list's stage filter chip itself, named after the pipeline. */}
              <FieldChip
                key={pipeline.id}
                field={{
                  name: 'stageId',
                  label: pipeline.name,
                  type: 'single_select',
                  // Only open stages: a project joins a pipeline to be worked, not already won or lost.
                  options: stageOptions(pipeline).filter((o) => o.group === 'open'),
                }}
                conditions={stageId ? [{ field: 'stageId', op: 'in', value: [stageId] }] : []}
                onChange={(next) => {
                  const c = next.find((x) => x.field === 'stageId');
                  const v: unknown = Array.isArray(c?.value) ? c.value[0] : c?.value;
                  setStageId(typeof v === 'string' ? v : undefined);
                }}
                single
                block
              />
            </div>
          )}
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={add.isPending || !pipelineId || !stageId}
              data-testid="bulk-apply"
            >
              {add.isPending && <Loader2 className="animate-spin" />} Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const REASON = {
  already_in_pipeline: 'already in it',
  not_found: 'not found',
  no_permission: 'no permission',
} as const;

/** "Added 3 · 2 skipped (already in it; no permission)" */
export function describeAddResult(r: BulkAddResult): string {
  const parts = [`Added ${r.added}`];
  if (r.skipped.length) {
    const reasons = [...new Set(r.skipped.map((s) => REASON[s.reason]))].join('; ');
    parts.push(`${r.skipped.length} skipped (${reasons})`);
  }
  return parts.join(' · ');
}
